import { Option } from '../../../shared/models/option.model';

export interface Model182Validation {
  key: string | null;
  type: string;
  message: string;
}

export interface Model182Declarado {
  uid: string | null;
  origen: string;
  nif: string;
  nifRepresentante: string;
  nombre: string;
  provincia: string;
  clave: string;
  porcentajeDeduccion: number | null;
  importe: number;
  especie: boolean;
  ccaa: string;
  porcentajeCcaa: number | null;
  naturalezaDeclarado: string;
  revocacion: boolean;
  ejercicioRevocada: number | null;
  tipoBien: string;
  identificacionBien: string;
  recurrencia: string;
  nifTitular: string;
  nombreTitular: string;
  documentos: number;
}

export interface Model182 {
  modelo: string;
  periodo?: string;
  ejercicio: number;
  sujetoNIF: string;
  sujetoDenominacion: string;
  naturalezaDeclarante: string;
  telefono: string;
  contacto: string;
  complementaria: boolean;
  sustitutiva: boolean;
  justificanteAnterior: string;
  justificante: string;
  declarados: Model182Declarado[];
  numeroRegistros: number;
  importeTotal: number;
  avisos: string[];
}

export interface Model182FieldOriginQuery {
  campo: string;
  taxYear: number;
  nif: string;
  contactUid: string | null;
  valorEnElFormulario: number;
}

export function nuevoDeclarado182(): Model182Declarado {
  return normalizarDeclarado182({} as Model182Declarado);
}

export function normalizarDeclarado182(origen: Model182Declarado): Model182Declarado {
  const d: any = origen || {};

  return {
    uid: d.uid || null,
    origen: d.origen || 'MANUAL',
    nif: d.nif || '',
    nifRepresentante: d.nifRepresentante || '',
    nombre: d.nombre || '',
    provincia: d.provincia || '',
    clave: d.clave || 'A',
    porcentajeDeduccion: d.porcentajeDeduccion ?? null,
    importe: Number(d.importe) || 0,
    especie: !!d.especie,
    ccaa: d.ccaa || '',
    porcentajeCcaa: d.porcentajeCcaa ?? null,
    naturalezaDeclarado: d.naturalezaDeclarado || 'F',
    revocacion: !!d.revocacion,
    ejercicioRevocada: d.ejercicioRevocada ?? null,
    tipoBien: d.tipoBien || '',
    identificacionBien: d.identificacionBien || '',
    recurrencia: d.recurrencia || '',
    nifTitular: d.nifTitular || '',
    nombreTitular: d.nombreTitular || '',
    documentos: Number(d.documentos) || 0
  };
}

export function nuevoModelo182(ejercicio: number): Model182 {
  return normalizarModelo182({ ejercicio } as Model182);
}

export function normalizarModelo182(origen: Model182): Model182 {
  const m: any = origen || {};

  return {
    modelo: '182',
    periodo: m.periodo,
    ejercicio: Number(m.ejercicio) || 0,
    sujetoNIF: m.sujetoNIF || '',
    sujetoDenominacion: m.sujetoDenominacion || '',
    naturalezaDeclarante: m.naturalezaDeclarante || '1',
    telefono: m.telefono || '',
    contacto: m.contacto || '',
    complementaria: !!m.complementaria,
    sustitutiva: !!m.sustitutiva,
    justificanteAnterior: m.justificanteAnterior || '',
    justificante: m.justificante || '',
    declarados: (m.declarados || []).map((item: Model182Declarado) => normalizarDeclarado182(item)),
    numeroRegistros: Number(m.numeroRegistros) || 0,
    importeTotal: Number(m.importeTotal) || 0,
    avisos: m.avisos || []
  };
}

export const NATURALEZA_DECLARANTE_182: Option[] = [
  { label: '1 - Entidad beneficiaria de los incentivos de la Ley 49/2002', value: '1' },
  { label: '2 - Fundación o asociación de utilidad pública (art. 68.3.b LIRPF)', value: '2' },
  { label: '3 - Titular o administrador de un patrimonio protegido', value: '3' },
  { label: '4 - Partido político, federación, coalición o agrupación de electores', value: '4' }
];

export const TIPO_DECLARACION_182: Option[] = [
  { label: 'Normal', value: 'N' },
  { label: 'Complementaria', value: 'C' },
  { label: 'Sustitutiva', value: 'S' }
];

export const CLAVE_182: Option[] = [
  { label: 'A - Donativos no prioritarios de mecenazgo', value: 'A' },
  { label: 'B - Donativos de actividades prioritarias de mecenazgo', value: 'B' },
  { label: 'C - Aportación al patrimonio protegido', value: 'C' },
  { label: 'D - Disposición del patrimonio protegido', value: 'D' },
  { label: 'E - Gasto de dinero y consumo de bienes fungibles', value: 'E' },
  { label: 'F - Cuotas de afiliación y aportaciones a partidos', value: 'F' },
  { label: 'G - Donaciones a partidos', value: 'G' }
];

export const NATURALEZA_DECLARADO_182: Option[] = [
  { label: 'F - Persona física', value: 'F' },
  { label: 'J - Persona jurídica', value: 'J' },
  { label: 'E - Entidad en régimen de atribución de rentas', value: 'E' }
];

export const TIPO_BIEN_182: Option[] = [
  { label: 'I - Inmueble', value: 'I' },
  { label: 'V - Valores mobiliarios', value: 'V' },
  { label: 'O - Otros', value: 'O' }
];

export const RECURRENCIA_182: Option[] = [
  { label: '1 - Donaba en los dos ejercicios anteriores', value: '1' },
  { label: '2 - No donaba en los dos ejercicios anteriores', value: '2' }
];

export const CCAA_182: Option[] = [
  { label: '01 - Andalucía', value: '01' },
  { label: '02 - Aragón', value: '02' },
  { label: '03 - Principado de Asturias', value: '03' },
  { label: '04 - Illes Balears', value: '04' },
  { label: '05 - Canarias', value: '05' },
  { label: '06 - Cantabria', value: '06' },
  { label: '07 - Castilla-La Mancha', value: '07' },
  { label: '08 - Castilla y León', value: '08' },
  { label: '09 - Cataluña', value: '09' },
  { label: '10 - Extremadura', value: '10' },
  { label: '11 - Galicia', value: '11' },
  { label: '12 - Madrid', value: '12' },
  { label: '13 - Región de Murcia', value: '13' },
  { label: '16 - La Rioja', value: '16' },
  { label: '17 - Comunidad Valenciana', value: '17' }
];
